import type {
  ConnectorConfig,
  ConnectorCredentials,
  SerializedConnectorHistory,
  ConnectorId,
  CreateIntegrationDto,
  SerializedConnector,
  SerializedIntegration,
  SerializedRun,
  SerializedRunDetail,
  UpdateIntegrationDto,
} from '@enmon/commons/lib/integrations';
import { BaseApiClient } from '../baseApiClient';

export interface IntegrationsFilter {
  customers?: string[];
  connectorId?: ConnectorId;
}

export interface CreateIntegrationArg {
  values: CreateIntegrationDto;
}

export interface UpdateIntegrationArg {
  integrationId: SerializedIntegration['_id'];
  values: UpdateIntegrationDto;
}

export interface PutIntegrationConfigArg {
  integrationId: SerializedIntegration['_id'];
  config: ConnectorConfig;
}

export interface DeleteIntegrationArg {
  integrationId: SerializedIntegration['_id'];
}

export interface PutIntegrationCredentialsArg {
  integrationId: SerializedIntegration['_id'];
  credentials: ConnectorCredentials;
}

export interface HealthFilter {
  limit?: number;
}

export interface RunsFilter {
  integrationId: SerializedIntegration['_id'];
  limit?: number;
}

export interface RunArg {
  integrationId: SerializedIntegration['_id'];
  runId: SerializedRun['_id'];
}

export type GetConnectorsResponse = SerializedConnector[];
export type GetIntegrationsResponse = SerializedIntegration[];
export type GetIntegrationResponse = SerializedIntegration;
export type CreateIntegrationResponse = SerializedIntegration;
export type UpdateIntegrationResponse = SerializedIntegration;
export type GetConnectorHealthResponse = SerializedConnectorHistory[];
export type GetRunsResponse = SerializedRun[];
export type GetRunResponse = SerializedRunDetail;

export class BackendGatewayIntegrationsApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('enmon-tools/integrations', undefined, jwt);
  }

  public async findConnectors(signal?: AbortSignal): Promise<GetConnectorsResponse> {
    const { data } = await this.axios.get<GetConnectorsResponse>('connectors', { signal });

    return data;
  }

  public async findAll(filter: IntegrationsFilter, signal?: AbortSignal): Promise<GetIntegrationsResponse> {
    const { data } = await this.axios.get<GetIntegrationsResponse>('', { params: filter, signal });

    return data;
  }

  public async findOne(
    integrationId: SerializedIntegration['_id'],
    signal?: AbortSignal,
  ): Promise<GetIntegrationResponse> {
    const { data } = await this.axios.get<GetIntegrationResponse>(integrationId, { signal });

    return data;
  }

  public async create({ values }: CreateIntegrationArg): Promise<CreateIntegrationResponse> {
    const { data } = await this.axios.post<CreateIntegrationResponse>('', values);

    return data;
  }

  public async update({ integrationId, values }: UpdateIntegrationArg): Promise<UpdateIntegrationResponse> {
    const { data } = await this.axios.patch<UpdateIntegrationResponse>(integrationId, values);

    return data;
  }

  public async putConfig({ integrationId, config }: PutIntegrationConfigArg): Promise<UpdateIntegrationResponse> {
    const { data } = await this.axios.put<UpdateIntegrationResponse>(`${integrationId}/config`, config);

    return data;
  }

  /** Write-only: credentials go in and never come back out. */
  public async putCredentials({ integrationId, credentials }: PutIntegrationCredentialsArg): Promise<void> {
    await this.axios.put(`${integrationId}/credentials`, credentials);
  }

  /** Removes the integration and everything it holds, credentials and run history included. */
  public async delete({ integrationId }: DeleteIntegrationArg): Promise<void> {
    await this.axios.delete(integrationId);
  }

  public async findConnectorHealth(
    filter: HealthFilter,
    signal?: AbortSignal,
  ): Promise<GetConnectorHealthResponse> {
    const { data } = await this.axios.get<GetConnectorHealthResponse>('health', { params: filter, signal });

    return data;
  }

  public async findRuns({ integrationId, limit }: RunsFilter, signal?: AbortSignal): Promise<GetRunsResponse> {
    const { data } = await this.axios.get<GetRunsResponse>(`${integrationId}/runs`, { params: { limit }, signal });

    return data;
  }

  public async findRun({ integrationId, runId }: RunArg, signal?: AbortSignal): Promise<GetRunResponse> {
    const { data } = await this.axios.get<GetRunResponse>(`${integrationId}/runs/${runId}`, { signal });

    return data;
  }
}
